/* ════════════ THE DISSOLVE ════════════
   The monero page's atmosphere: hex glyphs surface out of the dark,
   hold for a breath, then dissolve back into nothing — transactions
   you were never going to read anyway. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function mist(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#mist")!;
  const ctx = c.getContext("2d")!;

  const GLYPHS = "0123456789abcdefɱ█▓▒░";
  const ORANGE = "#ff6600";
  const GREY = "#9aa0a6";

  type Mote = {
    x: number;
    y: number;
    t: number; // age in frames
    life: number;
    size: number;
    col: string;
    text: string;
  };
  let motes: Mote[] = [];

  const spawn = (): Mote => {
    // mostly grey static; the occasional orange ɱ surfaces
    const isMark = Math.random() < 0.12;
    const len = isMark ? 1 : 2 + ((Math.random() * 6) | 0);
    let text = "";
    for (let i = 0; i < len; i++)
      text += isMark ? "ɱ" : GLYPHS[(Math.random() * GLYPHS.length) | 0];
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      t: 0,
      life: 140 + Math.random() * 220,
      size: isMark ? 15 + Math.random() * 12 : 10 + Math.random() * 4,
      col: isMark ? ORANGE : GREY,
      text,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(70, Math.floor((c.width * c.height) / 26000));
    motes = Array.from({ length: count }, () => {
      const m = spawn();
      m.t = Math.random() * m.life; // desync
      return m;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const m of motes) {
      const p = m.t / m.life;
      // fade in fast, hold, dissolve slow
      const a = p < 0.2 ? p / 0.2 : p > 0.55 ? (1 - p) / 0.45 : 1;
      ctx.globalAlpha = a * (m.col === ORANGE ? 0.5 : 0.22);
      ctx.fillStyle = m.col;
      ctx.font = `${m.size}px "IBM Plex Mono", monospace`;
      // the dissolve: characters decay into blocks as the mote ages
      const txt = p > 0.7 ? m.text.replaceAll(/[^░▒]/g, p > 0.85 ? "░" : "▒") : m.text;
      ctx.fillText(txt, m.x, m.y);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < motes.length; i++) {
      const m = motes[i]!;
      m.t++;
      m.y -= 0.08; // barely drifting up, like smoke
      if (m.t >= m.life) motes[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(mist);
